import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import React, { useEffect, useState } from "react";
import { API_ENDPOINTS } from "../apiConfig";
import { HomePage } from "../interfaces/Webcontent";
import { getAppSurfaceStyles } from "../theme/siteTheme";
import LeadSection from "../sections/LeadSection";

const HomepageComponent: React.FC = () => {
  const [content, setContent] = useState<HomePage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const theme = useTheme();
  const styles = getAppSurfaceStyles(theme);

  useEffect(() => {
    const fetchContent = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(API_ENDPOINTS.homepageContent);
        if (!res.ok) throw new Error("Fehler beim Laden der Startseite.");
        const text = await res.text();
        if (text.trim().startsWith("<")) {
          throw new Error(
            "Fehler: Die Startseiten-API liefert kein JSON (sondern HTML). Bitte Backend prüfen."
          );
        }
        const data = JSON.parse(text);
        setContent(data);
      } catch (e: any) {
        setError(e.message || "Unbekannter Fehler");
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  if (loading)
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  if (error)
    return (
      <Box sx={{ color: "#b71c1c", textAlign: "center", py: 6 }}>{error}</Box>
    );
  if (!content) return null;

  return (
    <Box>
      {/* Inline style for all links inside homepage content */}
      <style>{`
        .homepage-content a {
          color: ${theme.palette.primary.main} !important;
          cursor: pointer !important;
          text-decoration: none;
          border-bottom: 1px solid rgba(35, 35, 58, 0.2);
        }
      `}</style>
      <Box className="homepage-content" sx={{ mb: 4, textAlign: "center" }}>
        <Typography variant="h2" component="h1">
          {content.title}
        </Typography>
        {content.subtitle && (
          <Typography variant="h5" sx={{ color: "text.secondary", mb: 2 }}>
            {content.subtitle}
          </Typography>
        )}
        {content.welcome_text_small && (
          <Typography
            variant="h6"
            sx={{ color: "text.primary", mb: 1 }}
            component="div"
            dangerouslySetInnerHTML={{ __html: content.welcome_text_small }}
          />
        )}
        {content.welcome_text_small_two && (
          <Typography
            variant="h6"
            sx={{ color: "text.primary", mb: 2 }}
            component="div"
            dangerouslySetInnerHTML={{ __html: content.welcome_text_small_two }}
          />
        )}
      </Box>
      {content.image && (
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <img
            src={content.image}
            alt="Dartliga Württemberg"
            style={{ maxHeight: "200px" }}
          />
        </Box>
      )}
      <Box className="homepage-content" sx={{ mb: 3 }}>
        {content.paragraphs &&
          content.paragraphs.map((para, idx) => (
            <Typography
              key={idx}
              variant="body1"
              sx={{ mb: 2, fontSize: "1.15rem" }}
              component="div"
              dangerouslySetInnerHTML={{ __html: para }}
            />
          ))}
      </Box>
      <Box sx={styles.calloutPanel}>
        <LeadSection />
        <Typography
          variant="body1"
          component="div"
          sx={{
            mt: 4,
            fontSize: "1.15rem",
            color: "text.primary",
            textAlign: "center",
          }}
          dangerouslySetInnerHTML={{ __html: content.paragraph_ligaleitung }}
        />
      </Box>
    </Box>
  );
};

export default HomepageComponent;
